import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import { ChartLineSmooth, Download, TableSplit } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useParsedCsvFromMessage } from 'bones/components/molecules/Voicebox/hooks/useParsedCsvFromMessage';
import { VoiceboxPanelChart } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelChart';
import { VoiceboxCsvTable } from 'bones/components/molecules/Voicebox/shared/VoiceboxCsvTable';
import { VoiceboxMarkdown } from 'bones/components/molecules/Voicebox/shared/VoiceboxMarkdown';
import {
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
} from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Accordion } from 'bones/design-system/components/Accordion';
import { Button } from 'bones/design-system/components/Button';
import { LoadingIcon } from 'bones/design-system/components/icons/LoadingIcon';
import { Tab } from 'bones/design-system/components/tabs/Tab';
import { Tabs } from 'bones/design-system/components/tabs/Tabs';
import { Tooltip } from 'bones/design-system/components/Tooltip';

export interface VoiceboxPanelMessageSummaryProps {
  isChartOpen?: boolean;
  message: VoiceboxResponseMessage;
  messageIdx: number;
  settings: VoiceboxSettings;
}

export const VoiceboxPanelMessageSummary: React.VFC<
  VoiceboxPanelMessageSummaryProps
> = ({ isChartOpen, message, messageIdx, settings }) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { downloadCsv, loadingDownloadMessageId } = useVoiceboxConversation();
  const { setPanelHistory, requestChart } = useVoiceboxPanelData();

  const sparqlQueryActions = message.actions?.filter(
    (action) => action.type === VoiceboxResponseActionType.SPARQL
  );
  const hasSparqlAction = Boolean(sparqlQueryActions?.length);

  const parsedCsv = useParsedCsvFromMessage(message);

  return (
    <>
      <Accordion
        actions={
          <>
            {hasSparqlAction ? (
              <Tooltip
                description={copy.panel.messageSummary.download}
                position={BlueprintPopoverPosition.BOTTOM}
              >
                <Button
                  disabled={Boolean(loadingDownloadMessageId)}
                  leftIcon={
                    loadingDownloadMessageId === message.messageId
                      ? LoadingIcon
                      : Download
                  }
                  onClick={() => {
                    downloadCsv(message, settings);
                  }}
                  size="sm"
                  testId={testIds.panel.messageSummary.download}
                  variant="ghost-gray"
                />
              </Tooltip>
            ) : null}
            {parsedCsv ? (
              <Tabs size="sm" variant="square">
                <Tooltip
                  description={copy.panel.messageSummary.tabs.table}
                  position={BlueprintPopoverPosition.BOTTOM}
                >
                  <Tab
                    leftIcon={TableSplit}
                    onClick={() => {
                      setPanelHistory((prev) => {
                        const next = prev?.slice(0);
                        const last = next?.pop();

                        // istanbul ignore if
                        if (!next || !last) {
                          return prev;
                        }

                        return [...next, { ...last, isChartOpen: false }];
                      });
                    }}
                    selected={!isChartOpen}
                    size="sm"
                    testId={testIds.panel.messageSummary.tabs.table}
                  />
                </Tooltip>
                <Tooltip
                  description={copy.panel.messageSummary.tabs.chart}
                  position={BlueprintPopoverPosition.BOTTOM}
                >
                  <Tab
                    leftIcon={ChartLineSmooth}
                    onClick={() => {
                      requestChart(message.messageId);
                      setPanelHistory((prev) => {
                        const next = prev?.slice(0);
                        const last = next?.pop();

                        // istanbul ignore if
                        if (!next || !last) {
                          return prev;
                        }

                        return [...next, { ...last, isChartOpen: true }];
                      });
                    }}
                    selected={isChartOpen}
                    size="sm"
                    testId={testIds.panel.messageSummary.tabs.chart}
                  />
                </Tooltip>
              </Tabs>
            ) : null}
          </>
        }
        className="vb-voicebox-panel-message-summary"
        icon="left"
        initialIsOpen
        label={copy.panel.messageSummary.accordion}
        size="md"
        testId={testIds.panel.messageSummary.accordion}
      >
        <VoiceboxMarkdown
          linkTestId={testIds.panel.messageSummary.link}
          markdown={message.result}
          messageId={message.messageId}
          settings={settings}
        />
      </Accordion>

      {isChartOpen ? <VoiceboxPanelChart message={message} /> : null}

      {!isChartOpen && parsedCsv ? (
        <VoiceboxCsvTable
          message={message}
          messageIdx={messageIdx}
          parsed={parsedCsv}
          settings={settings}
        />
      ) : null}
    </>
  );
};
